import { useQuery } from "@tanstack/react-query";
import { get } from "api/api-methods";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useParams } from "react-router-dom";
import moment from "moment";
import { getSearchParams } from "search-params";
import { ExperienceViewResponseDto } from "types/experience-view";
import { getBookingWindowTimeUnit } from "utils/datepicker-utils";
import { dateToString } from "utils/moment-utils";
import { mapExperienceViewResponse } from "./experience-view-utils";

type ExperienceViewParams = Record<string, unknown> & {
    from: string;
    to?: string;
    tag?: string;
    invite?: string;
};

function useExperienceView(enabled: boolean = true) {
    const { unitId } = useParams();
    const { data: settings } = useExternalBookingSettings();
    const { tag, sectionIds, invite } = getSearchParams();

    const today = moment().startOf("day");
    const from = dateToString(today);
    const to = settings
        ? dateToString(
              today
                  .clone()
                  .add(
                      settings.bookingWindowAsMonth,
                      getBookingWindowTimeUnit(settings.bookingWindowUnit)
                  )
                  .subtract(1, "day")
          )
        : undefined;

    const params: ExperienceViewParams = { from, to };
    if (tag) {
        params.tag = tag;
    }
    if (invite) {
        params.invite = invite;
    }

    return useQuery({
        queryKey: ["experienceView", unitId, from, to, tag, sectionIds, invite],
        queryFn: async () => {
            const response = await get<ExperienceViewResponseDto>(
                `experience/unit/${unitId}/overview`,
                { params }
            );
            return mapExperienceViewResponse(response, sectionIds);
        },
        enabled: !!unitId && enabled && !!settings,
    });
}

export { useExperienceView };
